import { useCallback, useEffect, useState } from "react";
import { Col, Empty, Form, Image, Row, Select, Spin, Typography } from "antd";
import QRCode from "qrcode";
import styled from "styled-components";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { getCities, getDarkStores } from "store/admin-slice/adminServicesSlice";

const QR_SIZE = 320;
const QR_PLATFORM = "DELIVERY_APP";
// Fallback when `deliveryQrValidityTime` is missing from the init config
const DEFAULT_QR_VALIDITY_SECONDS = 10;

const QRContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const RefreshLabel = styled(Typography.Text)`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
`;

interface QRPayload {
  platform: string;
  timeStamp: string;
  storeId: string;
}

const buildQRPayload = (storeId: string, validitySeconds: number): QRPayload => ({
  platform: QR_PLATFORM,
  // Expiry time: current epoch milliseconds + validity window (epoch is
  // timezone-independent, so this is the same instant the IST clock shows)
  timeStamp: String(Date.now() + validitySeconds * 1000),
  storeId: String(storeId),
});

const generateQRDataUrl = (storeId: string, validitySeconds: number): Promise<string> =>
  QRCode.toDataURL(JSON.stringify(buildQRPayload(storeId, validitySeconds)), {
    margin: 1,
    width: QR_SIZE,
    color: { dark: "#000000", light: "#ffffff" },
  });

const DeliveryLoginQRPage = () => {
  useDocumentTitle("Delivery Login QR");
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [cityId, setCityId] = useState<string>();
  const [storeId, setStoreId] = useState<string>();
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const { darkStores, cities } = useAppSelector((state) => state.adminServices);
  const { initConfig } = useAppSelector((state) => state.app);

  const validitySeconds = (() => {
    const configured = Number(initConfig?.deliveryQrValiditySeconds);
    return Number.isFinite(configured) && configured > 0
      ? configured
      : DEFAULT_QR_VALIDITY_SECONDS;
  })();

  useEffect(() => {
    dispatch(getCities());
    dispatch(getDarkStores());
  }, [dispatch]);

  const refreshQR = useCallback(
    (selectedStoreId: string) =>
      generateQRDataUrl(selectedStoreId, validitySeconds)
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(undefined)),
    [validitySeconds]
  );

  // Generate the QR immediately on store selection, then refresh it every
  // `validitySeconds` — a single 1s ticker drives both countdown and refresh.
  useEffect(() => {
    if (!storeId) {
      setQrDataUrl(undefined);
      return;
    }

    refreshQR(storeId);
    setSecondsLeft(validitySeconds);

    const ticker = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          refreshQR(storeId);
          return validitySeconds;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(ticker);
  }, [storeId, validitySeconds, refreshQR]);

  const handleCityChange = (newCityId: string | number) => {
    setCityId(String(newCityId));
    setStoreId(undefined);
    form.setFieldsValue({ storeId: undefined });
  };

  const cityStores = (darkStores ?? []).filter(
    (store) => store?.cityId?.toString() === cityId
  );

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Delivery Login QR" />
        </Col>
      </Row>
      <Form form={form} layout="vertical" className="mb-8">
        <Row gutter={24}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="City" name="cityId" className="mb-4">
              <Select
                showSearch
                placeholder="Select city"
                className="w-full"
                filterOption={(input, option) => {
                  return option?.children?.toString()
                    .toLowerCase()
                    .includes(input?.toLowerCase() ?? "") ?? false;
                }}
                onChange={handleCityChange}
              >
                {(cities ?? []).map((city) => (
                  <Select.Option value={city.id} key={city.id}>
                    {city.name} || {city.id}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {cityId && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Store" name="storeId" className="mb-4">
                <Select
                  showSearch
                  placeholder="Select store"
                  className="w-full"
                  filterOption={(input, option) => {
                    return option?.children?.toString()
                      .toLowerCase()
                      .includes(input?.toLowerCase() ?? "") ?? false;
                  }}
                  onChange={(value: string | number) => setStoreId(String(value))}
                >
                  {cityStores.map((storeInfo) => (
                    <Select.Option value={storeInfo.id} key={storeInfo.id}>
                      {storeInfo.name} || {storeInfo.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {storeId ? (
        <QRContainer>
          {qrDataUrl ? (
            <>
              <Image
                src={qrDataUrl}
                width={QR_SIZE}
                height={QR_SIZE}
                preview={false}
                alt={`Delivery login QR for store ${storeId}`}
              />
              <Typography.Text strong>Store ID: {storeId}</Typography.Text>
              <RefreshLabel>
                QR refreshes in {secondsLeft}s
              </RefreshLabel>
            </>
          ) : (
            <Spin tip="Generating QR..." />
          )}
        </QRContainer>
      ) : (
        <Empty description="Select a city and store to generate the login /Trip End /Checkout QR." />
      )}
    </>
  );
};

export default DeliveryLoginQRPage;
